import React, { useEffect, useMemo, useRef, useState } from 'react';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');

// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];

  const script = tokens.map(token=>token[1]);

  // Estimate speech and pauses separately when measured words are unavailable.

  // Paragraph breaks give each narrated detail time to remain highlighted.

  const speechSeconds = .4;

  const pauses = tokens.map(([_,word,space])=>

    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));

  const weights = pauses.map(pause=>speechSeconds+pause);

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);

    const scale = totalWeight ? (end-start)/totalWeight : 0;

    let cursor = start;

    for (let k = from; k < index; k += 1) {

      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};

      cursor += weights[k]*scale;

    }

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}

function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}

// Keep the same timing-input aliases and stable timeline as What Is Tribe.

function useNarrationTimeline(narration, wordTimings, duration) {

  const timingKey = JSON.stringify(Array.isArray(wordTimings)

    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);

  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);

}

// Read the audio clock each frame. Typing adds character boundaries so it

// stays in sync with speech, pause, seeking, and playback rate changes.

function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    const playbackChanged = controller.current.playbackId !== playbackId;

    if (!media) {

      const stamp = performance.now();

      const anchor = parentAnchor.current;

      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null

        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);

      // Anchor pause/resume to the elapsed clock instead of resetting to zero.

      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};

    }

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    if (controller.current.playbackId !== playbackId) {

      controller.current.playbackId = playbackId;

      controller.current.syncId += 1;

    }

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}

// Emphasis is presentation-only. Match whole words without depending on

// capitalization or punctuation; an absent emphasis phrase is simply skipped.

function emphasisIndexes(words, emphasis) {

  const source = words.map(normalise);

  const strong = new Set();

  for (const phrase of Array.isArray(emphasis) ? emphasis : []) {

    if (typeof phrase !== 'string') continue;

    const target = phrase.split(/\s+/).filter(Boolean).map(normalise).filter(Boolean);

    if (!target.length) continue;

    const offset = source.findIndex((_, index) => target.every((word, i) => source[index + i] === word));

    if (offset < 0) continue;

    target.forEach((_, index) => strong.add(offset + index));

  }

  return strong;

}



// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay

// fixed between clock updates, so CSS completes the gentle rise smoothly.

function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {

  const words = text.split(/\s+/).filter(Boolean);

  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);

  const strong = emphasisIndexes(words, emphasis);

  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>

    <span className={`payments-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{

      animationDelay: `${complete || settled ? -10 : (delay + index * .09) / clock.rate}s`,

      animationDuration: `${.38 / clock.rate}s`,

      animationPlayState: complete || clock.playing ? 'running' : 'paused',

    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

  </React.Fragment>)}</Tag>;

}









function buildPaymentCues(narration,timeline) {

  return {

    typesAt:cueTime(narration,timeline,'your payment for hosting'),

    stipendAt:cueTime(narration,timeline,'The stipend covers'),

    hostingAt:cueTime(narration,timeline,'Your hosting payment is'),

    guestsAt:cueTime(narration,timeline,'You will never be expected'),

    reviewAt:timeline.words[timeline.words.length-1]?.end??Infinity,

  };

}

// This lesson owns its visuals, narration metadata, and practice check.

export default function Payments(props={}) {

  const narration=Payments.parts[0].narration;

  const speechTimeline=useNarrationTimeline(narration,props.wordTimings??props.words??EMPTY_TIMINGS,props.duration??0);

  const cues=useMemo(()=>buildPaymentCues(narration,speechTimeline),[narration,speechTimeline]);

  const timeline=useMemo(()=>({...speechTimeline,animationBoundaries:Object.values(cues)}),[speechTimeline,cues]);

  const clock=useLessonClock({...props,playbackId:`${props.playbackId??0}:payments`},timeline);

  const complete=props.audioStatus==='Complete'||Boolean((props.audioElement??props.audioRef?.current)?.ended);

  const review=complete||clock.now>=cues.reviewAt;

  const ledger=complete||clock.now>=cues.stipendAt;

  const hosting=clock.now>=cues.hostingAt;

  const guests=clock.now>=cues.guestsAt;

  const intro=clock.now>=cues.typesAt?'Hosting payment. Event stipend.':'Two types of payment.';

  return <div className="host-background-slide host-payments-slide" aria-label={Payments.title}>

    <style>{PAYMENTS_STYLES}</style>

    <section className={`payments-scene ${ledger?'is-details':'is-caption'}`} data-scene={ledger?'payment-details':'intro'} data-timing-mode={timeline.mode}>

      {ledger?<div className={`payments-panels ${review?'is-reviewing':''}`} aria-label="Event stipend, hosting payment, and guest expenses">

        <div className={`payments-panel ${!review&&!hosting?'is-focused':''}`} aria-current={!review&&!hosting?'step':undefined}>

          <h2>Event stipend</h2>

          <p className="payments-value payments-stipend-value">Covers event<br />expenses</p>

          <p className="payments-detail">Sent before the event, when needed.</p>

        </div>

        <div className={`payments-panel ${!review&&hosting&&!guests?'is-focused':''}`} aria-current={!review&&hosting&&!guests?'step':undefined}>

          <h2>Hosting payment</h2>

          <p className="payments-value payments-rate"><strong>$80</strong><span>per event</span></p>

          <p className="payments-detail">Longer events may pay $120.<br />Paid through Zelle after the event.</p>

        </div>

        <div className={`payments-panel ${!review&&guests?'is-focused':''}`} aria-current={!review&&guests?'step':undefined}>

          <h2>Guest expenses</h2>

          <p className="payments-value payments-guest-value">Never pay for<br />other guests</p>

          <p className="payments-detail">Each guest covers their own expenses.</p>

        </div>

      </div>:<div className="payments-caption" key={`${intro}-${clock.syncId}`}>

        <PhraseReveal text={intro} clock={clock} />

      </div>}

    </section>

  </div>;

}



const PAYMENTS_STYLES = `

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');

.host-payments-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}

.host-payments-slide * {box-sizing:border-box;}

.host-payments-slide .payments-scene {display:flex;align-items:center;justify-content:center;min-height:530px;padding:36px 44px;}

.host-payments-slide .payments-caption {max-width:850px;text-align:center;}

.host-payments-slide .payments-caption h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(32px,4vw,50px);font-weight:700;line-height:1.15;letter-spacing:-.025em;}

.host-payments-slide .payments-panels {display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px;width:100%;max-width:1040px;}

.host-payments-slide .payments-panel {min-width:0;text-align:center;padding:30px 24px;background:#f4f4f3;border-radius:10px;}

.host-payments-slide .payments-panel>* {opacity:.35;transition:opacity 420ms ease;}

.host-payments-slide .payments-panel.is-focused>*,.host-payments-slide .is-reviewing .payments-panel>* {opacity:1;}

.host-payments-slide .payments-panel h2 {margin:0 0 26px;font-size:17px;font-weight:650;line-height:1.35;letter-spacing:-.02em;}

.host-payments-slide .payments-value {display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:92px;margin:0;font-family:Merriweather,Georgia,serif;font-weight:700;letter-spacing:-.03em;}

.host-payments-slide .payments-stipend-value,.host-payments-slide .payments-guest-value {font-size:clamp(22px,2.5vw,28px);line-height:1.3;}

.host-payments-slide .payments-rate strong {font-size:clamp(42px,5vw,56px);font-weight:700;line-height:1.15;letter-spacing:-.045em;}

.host-payments-slide .payments-rate span {margin-top:6px;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;font-weight:400;line-height:1.3;letter-spacing:0;}

.host-payments-slide .payments-detail {margin:26px 0 0;font-size:13px;line-height:1.7;}

.host-payments-slide .payments-reveal-word {display:inline-block;opacity:0;transform:translateY(.36em);animation:payments-word-in 380ms cubic-bezier(.18,.84,.22,1) both;}

.host-payments-slide .payments-reveal-word.is-emphasized {font-weight:900;}

@keyframes payments-word-in {to{opacity:1;transform:translateY(0);}}

.host-training .ht-lesson-layout:has(.host-payments-slide) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.host-payments-slide)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.host-payments-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:600px){

 .host-payments-slide .payments-scene {padding:28px 16px;}

 .host-payments-slide .payments-panels {gap:8px;}

 .host-payments-slide .payments-panel {padding:24px 10px;}

 .host-payments-slide .payments-panel h2 {font-size:14px;min-height:38px;margin-bottom:18px;}

 .host-payments-slide .payments-stipend-value,.host-payments-slide .payments-guest-value {font-size:18px;}

 .host-payments-slide .payments-rate strong {font-size:36px;}

 .host-payments-slide .payments-detail {font-size:12px;}

 .host-payments-slide .payments-caption h1 {font-size:clamp(28px,6vw,38px);}

}

@media(prefers-reduced-motion:reduce){

 .host-payments-slide .payments-reveal-word {animation:none!important;opacity:1;transform:none;}

 .host-payments-slide .payments-panel>* {transition:none;}

}

`;



Object.assign(Payments, {

  "slide": 13,

  "id": "payments",

  "title": "Payments",

  "section": "Operations",

  "parts": [

    {

      "part": 0,

      "narration": "There are two types of payment you’ll receive from Tribe: your payment for hosting and, when needed, an event stipend.\nThe stipend covers necessary event expenses, such as food, drinks, or tickets. We’ll confirm what it covers and send it before the event starts.\nYour hosting payment is $80 per event. For significantly longer events, it may increase to $120. We’ll confirm the amount beforehand and pay you through Zelle when the event ends.\nYou will never be expected to pay for other guests. For example, if an event includes dinner, each guest should pay for their own meal."

    }

  ],

  "summary": [

    "$80 per event, with longer events confirmed beforehand.",

    "Stipends arrive before the event when needed."

  ],

  "check": {

    "prompt": "An event includes dinner. Are you expected to pay for the other guests?",

    "choices": [

      "Yes, then request reimbursement.",

      "No. Guests pay for their own meals.",

      "Only if they arrived late."

    ],

    "correct": 1,

    "explanation": "You are never expected to pay for other guests. Tribe confirms what any event stipend covers before the event."

  }

});
